import React, { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { X } from 'lucide-react';
import { Orb, Particle, GameStatus, ItemType } from './types';
import { COLORS, GAME_DURATION, ORB_BASE_LIFESPAN } from './constants';
import OrbEntity from './components/OrbEntity';
import ParticleSystem from './components/ParticleSystem';
import { gameService } from '../../../services/gameService';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { getDopamineCommentary } from './services/geminiService';
import SEO from '../../Common/SEO';
import { useLanguage } from '../../../context/LanguageContext';
import { t } from '../../../i18n';
import { ConfirmModal } from '../../Common/ConfirmModal';

export const NeonPitchGameView: React.FC = () => {
    const { lang } = useLanguage();
    const { user, isLoading: isAuthLoading } = useAuth();
    const { showToast } = useToast();
    const [status, setStatus] = useState<GameStatus>(GameStatus.START);
    const [score, setScore] = useState(0);
    const scoreRef = useRef(0);
    const [combo, setCombo] = useState(0);
    const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
    const [orbs, setOrbs] = useState<Orb[]>([]);
    const [particles, setParticles] = useState<Particle[]>([]);
    const [fever, setFever] = useState(0); 
    const [isFeverMode, setIsFeverMode] = useState(false);
    const [announcement, setAnnouncement] = useState("");
    const [highScore, setHighScore] = useState(0);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    const spawnTimerRef = useRef<number | null>(null);
    const gameTimerRef = useRef<number | null>(null);
    const announcementTimerRef = useRef<number | null>(null);
    const fieldRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        scoreRef.current = score;
    }, [score]);

    useEffect(() => {
        const saved = localStorage.getItem('neon-pitch-highscore');
        if (saved) setHighScore(parseInt(saved));
    }, []);

    useEffect(() => {
        if (score > highScore) {
            setHighScore(score);
            localStorage.setItem('neon-pitch-highscore', score.toString());
        }
    }, [score, highScore]);

    const showAnnouncement = (msg: string) => {
        setAnnouncement(msg);
        if (announcementTimerRef.current) window.clearTimeout(announcementTimerRef.current);
        announcementTimerRef.current = window.setTimeout(() => setAnnouncement(""), 2000);
    };

    const createParticles = (x: number, y: number, color: string, count: number = 10) => {
        if (!fieldRef.current) return;
        
        const rect = fieldRef.current.getBoundingClientRect();
        const px = (x / 100) * rect.width;
        const py = (y / 100) * rect.height;
        
        const newParticles: Particle[] = [];
        for (let i = 0; i < count; i++) {
            newParticles.push({
                id: uuidv4(),
                x: px,
                y: py,
                vx: (Math.random() - 0.5) * 15,
                vy: (Math.random() - 0.5) * 15,
                life: 1.0,
                color: color,
                size: Math.random() * 4 + 2
            });
        }
        setParticles(prev => [...prev.slice(-50), ...newParticles]);
    };

    const spawnOrb = useCallback(() => {
        const rand = Math.random();
        let type: ItemType = 'ball';
        let color = COLORS.BALL;
        let radius = 45;
        let lifeSpan = ORB_BASE_LIFESPAN;

        if (rand < 0.1) { type = 'red'; color = COLORS.RED; }
        else if (rand < 0.2) { type = 'yellow'; color = COLORS.YELLOW; }
        else if (rand < 0.3) { type = 'offside'; color = COLORS.OFFSIDE; radius = 35; lifeSpan = 1200; }
        else if (rand < 0.45) { type = 'tackle'; color = COLORS.TACKLE; radius = 40; }
        else if (rand < 0.55) { type = 'sub'; color = COLORS.SUB; radius = 50; }
        else if (rand < 0.65) { type = 'shoot'; color = COLORS.SHOOT; radius = 60; lifeSpan = 1000; }

        const newOrb: Orb = {
            id: uuidv4(),
            x: 10 + Math.random() * 80,
            y: 15 + Math.random() * 70,
            radius: radius,
            color: color,
            createdAt: Date.now(),
            lifeSpan: isFeverMode ? lifeSpan * 1.5 : lifeSpan,
            type
        };

        setOrbs(prev => [...prev, newOrb]);
    }, [isFeverMode]);

    const handlePop = (id: string) => {
        setOrbs(prev => {
            const popped = prev.find(o => o.id === id);
            if (popped) {
                let delta = 0;
                let feverBoost = 3;
                let isBad = false;

                switch (popped.type) {
                    case 'ball': delta = 100; feverBoost = 5; break;
                    case 'shoot': delta = 500; feverBoost = 15; break;
                    case 'sub': delta = 200; feverBoost = 25; break;
                    case 'tackle': delta = 150; feverBoost = 8; break;
                    case 'yellow': delta = -200; feverBoost = -10; isBad = true; break;
                    case 'red': delta = -500; feverBoost = -20; isBad = true; break;
                    case 'offside': delta = -300; feverBoost = -15; isBad = true; break;
                }

                if (isFeverMode && !isBad) delta *= 2;
                
                const newScore = Math.max(0, scoreRef.current + delta);
                setScore(newScore);
                
                if (isBad) {
                    setCombo(0);
                    createParticles(popped.x, popped.y, '#ff0000', 30);
                    if (Math.abs(delta) >= 300) getDopamineCommentary(newScore, 0, 'penalty').then(showAnnouncement);
                } else {
                    const newCombo = combo + 1;
                    setCombo(newCombo);
                    setFever(f => Math.min(100, Math.max(0, f + feverBoost)));
                    createParticles(popped.x, popped.y, popped.color, 15);
                    if (newCombo % 10 === 0) getDopamineCommentary(newScore, newCombo, 'milestone').then(showAnnouncement);
                }

                return prev.filter(o => o.id !== id);
            }
            return prev;
        });
    };

    const startGame = () => {
        if (!user) {
            setIsLoginModalOpen(true);
            return;
        }

        showToast('info', t(lang, 'neon_pitch_deduction_toast'));
        setScore(0);
        setCombo(0);
        setTimeLeft(GAME_DURATION);
        setOrbs([]);
        setFever(0);
        setIsFeverMode(false);
        setStatus(GameStatus.PLAYING);
        showAnnouncement("⚽️ KICK OFF!! ⚽️");
    };

    const isSavingRef = useRef(false);
    const endGame = async () => {
        if (status !== GameStatus.PLAYING) return;
        
        const finalScore = scoreRef.current;
        setStatus(GameStatus.GAMEOVER);
        if (spawnTimerRef.current) window.clearInterval(spawnTimerRef.current);
        if (gameTimerRef.current) window.clearInterval(gameTimerRef.current);
        getDopamineCommentary(finalScore, combo, 'gameover').then(showAnnouncement);

        // Save score to database (once)
        if (isSavingRef.current) return;
        isSavingRef.current = true;

        try {
            const games = await gameService.getStoreGames();
            const neonPitchGame = games.find(g => g.title.toUpperCase().includes('NEON PITCH'));
            if (neonPitchGame) {
                await gameService.saveScore(neonPitchGame.id, finalScore);
            }
        } catch (error) {
            console.error('Failed to save score:', error);
        } finally {
            isSavingRef.current = false;
        }
    };

    // Game Timer
    useEffect(() => {
        if (status === GameStatus.PLAYING) {
            gameTimerRef.current = window.setInterval(() => {
                setTimeLeft(t => {
                    if (t <= 1) {
                        endGame();
                        return 0;
                    }
                    return t - 1;
                });
            }, 1000);
            return () => { if (gameTimerRef.current) window.clearInterval(gameTimerRef.current); };
        }
    }, [status]);

    // Fever Mode Logic
    useEffect(() => {
        if (fever >= 100 && !isFeverMode) {
            setIsFeverMode(true);
            getDopamineCommentary(score, combo, 'fever').then(showAnnouncement);
            const timer = window.setTimeout(() => {
                setIsFeverMode(false);
                setFever(0);
            }, 7000);
            return () => window.clearTimeout(timer);
        }
    }, [fever, isFeverMode]);

    // Spawn Loop
    useEffect(() => {
        if (status === GameStatus.PLAYING) {
            const interval = isFeverMode ? 250 : Math.max(300, 800 - (score / 500));
            spawnTimerRef.current = window.setInterval(spawnOrb, interval) as unknown as number;
            return () => { if (spawnTimerRef.current) window.clearInterval(spawnTimerRef.current); };
        }
    }, [status, score, spawnOrb, isFeverMode]);

    // Despawn logic (No life penalty for football 60s mode, just loss of potential score)
    useEffect(() => {
        if (status === GameStatus.PLAYING) {
            const checker = window.setInterval(() => {
                const now = Date.now();
                setOrbs(prev => prev.filter(o => now - o.createdAt < o.lifeSpan));
            }, 100);
            return () => window.clearInterval(checker);
        }
    }, [status]);

    if (isAuthLoading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-white/10 border-t-green-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    const handleLoginRedirect = () => {
        setIsLoginModalOpen(false);
        if (window.navigateTo) window.navigateTo('/login');
    };

    return (
        <div className="min-h-screen">
            <SEO 
                title={t(lang, 'neon_pitch_seo_title')}
                description={t(lang, 'neon_pitch_seo_description')}
                keywords={t(lang, 'neon_pitch_seo_keywords')}
            />
            <div className={`relative w-full h-[calc(100vh-64px)] md:h-screen flex flex-col items-center justify-center overflow-hidden transition-colors duration-700 ${isFeverMode ? 'bg-green-900/40' : 'bg-[#050505]'}`}>
      
                {/* Pitch Lines Background */}
                <div className="absolute inset-0 z-0 opacity-20 pointer-events-none border-[2px] border-green-500/30 m-4 flex items-center justify-center">
                    <div className="w-full h-[1px] bg-green-500/30 absolute top-1/2" />
                    <div className="w-48 h-48 border-[2px] border-green-500/30 rounded-full absolute" />
                </div>

                {/* HUD: Scoreboard */}
                <div className="absolute top-0 w-full p-6 flex justify-between items-start z-40 pointer-events-none font-orbitron">
                    <div className="flex flex-col bg-black/40 p-3 rounded-xl border border-white/10 backdrop-blur-md">
                        <span className="text-white/50 text-[10px] uppercase tracking-widest">{t(lang, 'neon_pitch_score')}</span>
                        <span className="text-4xl font-black text-white drop-shadow-[0_0_10px_#fff]">
                            {score.toLocaleString()}
                        </span>
                        <div className="mt-2 text-xs font-bold text-yellow-500">
                            {t(lang, 'neon_pitch_best')}: {highScore.toLocaleString()}
                        </div>
                    </div>

                    <div className="flex flex-col items-center bg-black/60 p-4 rounded-xl border-2 border-green-500/50 min-w-[120px]">
                        <span className="text-green-500 text-[10px] uppercase tracking-widest font-bold">{t(lang, 'neon_pitch_time_left')}</span>
                        <span className={`text-4xl font-black ${timeLeft <= 10 ? 'text-red-500 animate-pulse' : 'text-white'}`}>
                            0:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                        </span>
                    </div>
                </div>

                {/* Combo & Fever Meter */}
                <div className="absolute bottom-10 w-full px-10 flex flex-col items-center z-40 pointer-events-none font-orbitron">
                    {combo > 2 && (
                        <div className="mb-4 animate-bounce">
                            <span className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-t from-green-400 to-white italic">
                                {combo} {t(lang, 'neon_pitch_streak')}
                            </span>
                        </div>
                    )}
                    
                    <div className="w-full max-w-md h-4 bg-white/5 rounded-full overflow-hidden border border-white/10 p-0.5">
                        <div 
                            className={`h-full transition-all duration-300 ease-out rounded-full ${isFeverMode ? 'bg-gradient-to-r from-yellow-400 via-white to-yellow-400 fever-glow' : 'bg-gradient-to-r from-green-600 to-green-300 shadow-[0_0_15px_#22c55e]'}`}
                            style={{ width: `${isFeverMode ? 100 : fever}%` }}
                        />
                    </div>
                    <span className={`mt-2 text-[10px] tracking-[0.3em] ${isFeverMode ? 'text-yellow-400 font-bold animate-pulse' : 'text-white/20'}`}>
                        {isFeverMode ? t(lang, 'neon_pitch_fever_unleashed') : t(lang, 'neon_pitch_adrenaline_gauge')}
                    </span>
                </div>

                {/* Dynamic Commentary */}
                {announcement && (
                    <div className="absolute top-1/3 z-50 pointer-events-none transition-all animate-in fade-in zoom-in slide-in-from-top-10 duration-300">
                        <h2 className="text-4xl md:text-6xl font-black text-white font-orbitron text-center drop-shadow-[0_10px_20px_rgba(0,0,0,0.8)] px-4">
                            {announcement}
                        </h2>
                    </div>
                )}

                {/* Field Layer */}
                <div 
                    ref={fieldRef}
                    className="absolute inset-0 z-20 overflow-hidden" 
                    onClick={() => setCombo(0)}
                >
                    {orbs.map(orb => (
                        <OrbEntity key={orb.id} orb={orb} onPop={handlePop} />
                    ))}
                </div>

                {/* Visual Effects */}
                <ParticleSystem particles={particles} setParticles={setParticles} />

                {/* Menu Overlay */}
                {status === GameStatus.START && (
                    <div className="z-50 text-center space-y-4 p-8 bg-black/90 backdrop-blur-2xl border border-green-500/20 rounded-[40px] shadow-2xl max-w-sm mx-4">
                        <div className="flex justify-center mb-2">
                            <span className="text-4xl animate-bounce">⚽️</span>
                        </div>
                        <h1 className="text-2xl font-black text-white font-orbitron leading-tight">
                            NEON<span className="ml-2 text-green-400">PITCH</span>
                        </h1>
                        <div className="text-white/60 text-sm space-y-2 font-medium">
                            <p>{t(lang, 'neon_pitch_instructions')}</p>
                            <div className="grid grid-cols-2 gap-2 pt-4">
                                <div className="bg-white/5 p-2 rounded">⚽ +100</div>
                                <div className="bg-white/5 p-2 rounded">🚀 +500</div>
                                <div className="bg-red-500/20 p-2 rounded text-red-400">🟥 -500</div>
                                <div className="bg-yellow-500/20 p-2 rounded text-yellow-400">🟨 -200</div>
                            </div>
                        </div>
                        <button 
                            onClick={startGame}
                            className="w-full group relative px-10 py-4 bg-green-500 text-black font-black font-orbitron rounded-2xl md:hover:bg-white transition-all transform md:hover:scale-105 active:scale-95 overflow-hidden"
                        >
                            <span className="relative z-10">{t(lang, 'neon_pitch_kickoff')}</span>
                            <div className="absolute inset-0 bg-white/20 translate-x-[-100%] md:group-hover:translate-x-[100%] transition-transform duration-500" />
                        </button>

                        <button 
                            onClick={() => window.navigateTo('/gamestore')}
                            className="w-full py-4 text-white/40 md:hover:text-white text-xs font-orbitron tracking-widest transition-colors uppercase"
                        >
                            {t(lang, 'neon_pitch_exit')}
                        </button>
                    </div>
                )}

                {status === GameStatus.GAMEOVER && (
                    <div className="z-50 text-center space-y-6 p-12 bg-black/95 backdrop-blur-3xl border-4 border-white/10 rounded-[40px] shadow-2xl shadow-green-500/10">
                        <h2 className="text-2xl font-bold text-green-500 font-orbitron uppercase tracking-widest">{t(lang, 'neon_pitch_full_time')}</h2>
                        <div className="flex flex-col items-center">
                            <span className="text-white/40 text-[10px] font-orbitron uppercase mb-1">{t(lang, 'neon_pitch_total_score')}</span>
                            <span className="text-7xl font-black text-white font-orbitron drop-shadow-[0_0_20px_rgba(255,255,255,0.3)]">
                                {score.toLocaleString()}
                            </span>
                        </div>
                        <div className="text-xs text-white/40 font-orbitron">
                            {t(lang, 'neon_pitch_max_combo')}: {combo}
                        </div>
                        <button 
                            onClick={startGame}
                            className="w-full py-5 bg-white text-black font-black font-orbitron rounded-2xl md:hover:bg-green-400 transition-all transform md:hover:scale-105"
                        >
                            {t(lang, 'neon_pitch_rematch')}
                        </button>
                        
                        <button 
                            onClick={() => window.navigateTo('/gamestore')}
                            className="w-full py-4 text-white/40 md:hover:text-white text-xs font-orbitron tracking-widest transition-colors uppercase"
                        >
                            {t(lang, 'neon_pitch_exit')}
                        </button>
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onConfirm={handleLoginRedirect}
                title={t(lang, 'loginRequired')}
                description={t(lang, 'loginParticipate')}
                confirmLabel={t(lang, 'signInLabel')}
                cancelLabel={t(lang, 'cancel')}
            />
        </div>
    );
};
